import Phaser from "phaser";
import { sfx, unlockAudio } from "@shared/audio";
import { exposeBotHook, loadSave, writeSave } from "@kit/boot";
import { BG, FONT, TEXT_DIM, W, backdrop, centerCamera, confetti, floatText, overlay, share, text, type OverlayButton } from "@kit/ui";
import { currentLang, publicGameUrl } from "@kit/lang";
import { afterSkip, afterWin, mapBackLink, mapText, openMap, starLine } from "@kit/map";
import { recordSkip, recordWin } from "@kit/progress";
import { track } from "@kit/telemetry";
import { categoryText, formatText, language, tr } from "./i18n";
import { createGame, finishReveal, placeCard, type ZamanState } from "./core/game";
import { oracleBot } from "./core/bot";
import { generateLevel, type ZamanLevel } from "./core/generator";
import type { TimelineEvent } from "./core/events";

const ACCENT = 0x6a4c93;
const GREEN = 0x8ac926;
const ORANGE = 0xff924c;
const CARD_W = 652;
const TIMELINE_TOP = 220;
/** Timeline height and deck position on the 1280-tall design; taller phones get a longer timeline (`stretch`). */
const TIMELINE_H = 814;
const CARD_MIN_H = 74;
const CARD_MAX_H = 112;
const DECK_Y = 1192;
const MAX_FAILS = 3;

type Save = { level: number; attempts: Record<string, number>; bestMistakes: Record<string, number> };
type Point = { x: number; y: number };
type CardVisual = { container: Phaser.GameObjects.Container; border: Phaser.GameObjects.Graphics; year: Phaser.GameObjects.Text };

export class ZamanScene extends Phaser.Scene {
  private levelArg?: number;
  private level!: ZamanLevel;
  private state!: ZamanState;
  private save!: Save;
  private startedAt = 0;
  private ended = false;
  private runToken = 0;
  private statusLabel!: Phaser.GameObjects.Text;
  private promptLabel!: Phaser.GameObjects.Text;
  private deckContainer?: Phaser.GameObjects.Container;
  private overlayPanel?: Phaser.GameObjects.Container;
  private cardViews = new Map<string, CardVisual>();
  private slotZones: Phaser.GameObjects.Zone[] = [];
  private slotMarkers: Phaser.GameObjects.Container[] = [];
  private slotPositions: Point[] = [];
  private cardHeight = CARD_MAX_H;
  /** Extra timeline length on tall phones (the deck card moves down by the same amount). */
  private stretch = 0;
  private overlayNext: Point = { x: W / 2, y: 740 };
  private overlayShare: Point = { x: W / 2, y: 740 };

  constructor() {
    super("zaman");
  }

  init(data?: { level?: number }): void {
    this.levelArg = data?.level;
    this.ended = false;
    this.runToken++;
    this.cardViews.clear();
    this.slotZones = [];
    this.slotMarkers = [];
    this.slotPositions = [];
    this.deckContainer = undefined;
    this.overlayPanel = undefined;
    this.overlayNext = { x: W / 2, y: this.scale.height / 2 + 110 };
    this.overlayShare = { x: W / 2, y: this.scale.height / 2 + 110 };
  }

  create(): void {
    centerCamera(this);
    this.cameras.main.setBackgroundColor(BG);
    backdrop(this, { light: 0x6a4c93 });
    this.stretch = Math.round(Math.min(360, Math.max(0, this.scale.height - 1280) * 0.75));
    const save = loadSave<Save>("zaman", { level: 1, attempts: {}, bestMistakes: {} });
    this.save = { level: Math.max(1, save.level ?? 1), attempts: save.attempts ?? {}, bestMistakes: save.bestMistakes ?? {} };
    const requested = new URLSearchParams(location.search).get("level");
    const queryLevel = requested && /^\d+$/.test(requested) ? Number(requested) : undefined;
    const n = Math.max(1, Math.floor(this.levelArg ?? queryLevel ?? this.save.level));
    this.level = generateLevel(n);
    this.state = createGame(this.level);
    this.startedAt = Date.now();
    this.makeHeader(n);
    this.makeTimelineRail();
    this.promptLabel = text(this, W / 2, 1082 + this.stretch, tr("prompt"), 28, "#ffffff");
    text(this, W / 2, 1114 + this.stretch, tr("reminder"), 17, TEXT_DIM, false);
    this.statusLabel = text(this, W / 2, 178, "", 21, "#ffffff");
    this.refresh();
    track("level_start", { level: n });
    this.events.once("shutdown", () => {
      this.runToken++;
    });
    exposeBotHook({
      state: () => this.botState(),
      autoplay: () => this.autoplay(),
      share: () => this.shareScore(),
    });
  }

  private makeHeader(n: number): void {
    this.add.rectangle(W / 2, 0, W * 3, 150, 0x12152b).setOrigin(0.5, 0);
    this.add.rectangle(24, 26, 8, 96, ACCENT).setOrigin(0, 0).setAlpha(0.9);
    text(this, 48, 42, tr("title"), 31, "#ffffff").setOrigin(0, 0.5);
    mapBackLink(this, "zaman", 48, 101, TEXT_DIM, 19);
    text(this, W - 32, 31, tr("level"), 17, "#b8bee6").setOrigin(1, 0.5);
    text(this, W - 32, 86, String(n), 48, "#ffffff").setOrigin(1, 0.5);
    this.add.circle(W - 54, 122, 5, ACCENT).setAlpha(0.8);
  }

  private makeTimelineRail(): void {
    this.add.line(0, 0, 44, TIMELINE_TOP, 44, TIMELINE_TOP + this.timelineH(), 0x6a4c93, 0.28).setLineWidth(4);
    this.add.circle(44, TIMELINE_TOP, 8, ACCENT).setAlpha(0.8);
    this.add.circle(44, TIMELINE_TOP + this.timelineH(), 8, ACCENT).setAlpha(0.8);
  }

  private timelineH(): number {
    return TIMELINE_H + this.stretch;
  }

  private deckY(): number {
    return DECK_Y + this.stretch;
  }

  private layoutFor(count: number): { height: number; top: number } {
    const height = Math.max(CARD_MIN_H, Math.min(CARD_MAX_H, Math.floor(this.timelineH() / Math.max(1, count))));
    return { height, top: TIMELINE_TOP + (this.timelineH() - count * height) / 2 };
  }

  private cardY(index: number, count: number, height: number, top: number): number {
    return top + index * height + height / 2;
  }

  private slotY(index: number, count: number, height: number, top: number): number {
    return top + Math.max(0, Math.min(count, index)) * height;
  }

  private refresh(): void {
    this.statusLabel.setText(formatText("status", {
      moves: Math.min(this.state.cardsTotal, this.state.moves + (this.state.phase === "placing" ? 1 : 0)),
      total: this.state.cardsTotal,
      mistakes: this.state.mistakes,
    }));
    this.promptLabel.setText(tr("prompt"));
    this.drawTimeline();
    this.drawDeckCard();
  }

  private drawTimeline(movingId?: string, startY = this.deckY()): void {
    for (const zone of this.slotZones) zone.destroy();
    this.slotZones = [];
    for (const marker of this.slotMarkers) marker.destroy();
    this.slotMarkers = [];
    this.slotPositions = [];
    for (const visual of this.cardViews.values()) visual.container.destroy();
    this.cardViews.clear();

    const count = this.state.timeline.length;
    const layout = this.layoutFor(count);
    this.cardHeight = layout.height;
    this.state.timeline.forEach((event, index) => {
      const y = this.cardY(index, count, layout.height, layout.top);
      const animateThis = event.id === movingId;
      const visual = this.makePlacedCard(event, y, layout.height, animateThis);
      if (animateThis) visual.container.setY(startY).setScale(0.94);
      this.cardViews.set(event.id, visual);
    });

    const interactive = this.state.phase === "placing" && !this.ended;
    for (let index = 0; index <= count; index++) {
      const y = this.slotY(index, count, layout.height, layout.top);
      this.slotPositions.push({ x: W / 2, y });
      // Touch screens have no hover, so every gap is a clearly visible "+" target while placing.
      const line = this.add.graphics();
      const pill = this.add.graphics();
      const plus = this.add.text(0, 0, "+", { fontFamily: FONT, fontSize: "30px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0.5, 0.55);
      const marker = this.add.container(W / 2, y, [line, pill, plus]).setDepth(24).setVisible(interactive);
      this.slotMarkers.push(marker);
      const drawMarker = (active: boolean) => {
        line.clear();
        line.fillStyle(active ? 0xc4a7e6 : 0x8b72ad, active ? 0.95 : 0.55);
        line.fillRoundedRect(-CARD_W / 2, -2, CARD_W, 4, 2);
        pill.clear();
        pill.fillStyle(active ? 0x8a63bf : ACCENT, 1);
        pill.fillRoundedRect(-62, -20, 124, 40, 20);
        pill.lineStyle(3, 0xc4a7e6, 1).strokeRoundedRect(-62, -20, 124, 40, 20);
        marker.setScale(active ? 1.08 : 1);
      };
      drawMarker(false);
      if (interactive) this.tweens.add({ targets: pill, alpha: 0.72, duration: 700, yoyo: true, repeat: -1, ease: "Sine.InOut" });
      const zone = this.add.zone(W / 2, y, CARD_W, 88).setDepth(25).setSize(CARD_W, 88);
      if (interactive) {
        zone.setInteractive({ useHandCursor: true });
        zone.on("pointerover", () => drawMarker(true));
        zone.on("pointerout", () => drawMarker(false));
        zone.on("pointerdown", (_pointer: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
          event.stopPropagation();
          this.handleSlot(index);
        });
      }
      this.slotZones.push(zone);
    }
  }

  private makePlacedCard(event: TimelineEvent, y: number, height: number, hiddenYear: boolean): CardVisual {
    const move = this.state.history.find((entry) => entry.id === event.id);
    const revealedNow = this.state.phase === "revealing" && this.state.lastMove?.id === event.id;
    const borderColor = revealedNow ? ACCENT : !move ? ACCENT : move.correct ? GREEN : ORANGE;
    const fillColor = revealedNow || !move ? 0x292342 : move.correct ? 0x23384a : 0x3e3340;
    const width = CARD_W;
    const h = height - 3;
    const bg = this.add.graphics();
    bg.fillStyle(fillColor, 1).fillRoundedRect(-width / 2, -h / 2, width, h, 16);
    bg.lineStyle(3, borderColor, 0.96).strokeRoundedRect(-width / 2, -h / 2, width, h, 16);
    const label = this.add.text(-27, 0, eventText(event, language()), {
      fontFamily: FONT,
      fontSize: height <= 78 ? "20px" : "23px",
      color: "#ffffff",
      fontStyle: "bold",
      align: "center",
      wordWrap: { width: 500, useAdvancedWrap: true },
    }).setOrigin(0.5);
    const year = this.add.text(width / 2 - 54, 0, hiddenYear ? "" : this.formatYear(event.year), {
      fontFamily: FONT,
      fontSize: height <= 78 ? "21px" : "26px",
      color: hiddenYear ? "#ffffff" : event.year < 0 ? "#d6c7e9" : "#ffffff",
      fontStyle: "bold",
      align: "center",
    }).setOrigin(0.5);
    const container = this.add.container(W / 2, y, [bg, label, year]).setSize(width, h).setDepth(10);
    return { container, border: bg, year };
  }

  private drawDeckCard(): void {
    this.deckContainer?.destroy();
    this.deckContainer = undefined;
    if (!this.state.current || this.state.phase === "won" || this.state.phase === "lost") return;
    const event = this.state.current;
    const bg = this.add.graphics();
    bg.fillStyle(0x30294a, 1).fillRoundedRect(-CARD_W / 2, -50, CARD_W, 100, 20);
    bg.lineStyle(3, ACCENT, 1).strokeRoundedRect(-CARD_W / 2, -50, CARD_W, 100, 20);
    const title = this.add.text(0, -9, eventText(event, language()), {
      fontFamily: FONT,
      fontSize: "24px",
      color: "#ffffff",
      fontStyle: "bold",
      align: "center",
      wordWrap: { width: 570, useAdvancedWrap: true },
    }).setOrigin(0.5);
    const category = this.add.text(0, 31, formatText("categoryLine", { category: categoryText(event.category) }), {
      fontFamily: FONT,
      fontSize: "16px",
      color: "#d4c6e7",
      align: "center",
    }).setOrigin(0.5);
    this.deckContainer = this.add.container(W / 2, this.deckY(), [bg, title, category]).setSize(CARD_W, 100).setDepth(15);
  }

  private formatYear(year: number): string {
    if (year < 0) return currentLang() === "tr" ? `MÖ ${Math.abs(year)}` : `${Math.abs(year)} BC`;
    return String(year);
  }

  private async handleSlot(slotIndex: number): Promise<void> {
    if (this.state.phase !== "placing" || this.ended || !this.state.current) return;
    unlockAudio();
    const token = this.runToken;
    const current = this.state.current;
    const tappedY = this.slotPositions[slotIndex]?.y ?? this.deckY();
    const placed = placeCard(this.state, slotIndex);
    if (placed === this.state) {
      sfx.nope();
      return;
    }
    this.state = placed;
    this.deckContainer?.destroy();
    this.deckContainer = undefined;
    this.drawTimeline(current.id, this.deckY());
    const visual = this.cardViews.get(current.id);
    if (!visual) return;

    await this.tweenPromise({ targets: visual.container, y: tappedY, scale: 1, duration: 260, ease: "Back.Out" });
    if (token !== this.runToken) return;
    await this.tweenPromise({ targets: visual.container, scaleX: 0.05, duration: 90, ease: "Quad.In" });
    if (token !== this.runToken) return;
    visual.year.setText(this.formatYear(current.year));
    await this.tweenPromise({ targets: visual.container, scaleX: 1, duration: 150, ease: "Back.Out" });
    if (token !== this.runToken) return;

    if (placed.lastMove!.correct) {
      sfx.pop(6);
      floatText(this, W / 2, visual.container.y - 30, tr("plusOne"), "#8ac926", 46);
      const targetY = this.cardY(placed.lastMove!.actualSlot, placed.timeline.length, this.cardHeight, this.layoutFor(placed.timeline.length).top);
      visual.border.clear();
      visual.border.fillStyle(0x23384a, 1).fillRoundedRect(-CARD_W / 2, -this.cardHeight / 2 + 1.5, CARD_W, this.cardHeight - 3, 16);
      visual.border.lineStyle(4, GREEN, 1).strokeRoundedRect(-CARD_W / 2, -this.cardHeight / 2 + 1.5, CARD_W, this.cardHeight - 3, 16);
      await this.tweenPromise({ targets: visual.container, y: targetY, duration: 200, ease: "Back.Out" });
      if (token !== this.runToken) return;
      this.tweens.add({ targets: visual.container, scale: 1.055, duration: 120, yoyo: true, ease: "Back.Out" });
    } else {
      sfx.nope();
      const targetY = this.cardY(placed.lastMove!.actualSlot, placed.timeline.length, this.cardHeight, this.layoutFor(placed.timeline.length).top);
      visual.border.clear();
      visual.border.fillStyle(0x3e3340, 1).fillRoundedRect(-CARD_W / 2, -this.cardHeight / 2 + 1.5, CARD_W, this.cardHeight - 3, 16);
      visual.border.lineStyle(4, ORANGE, 1).strokeRoundedRect(-CARD_W / 2, -this.cardHeight / 2 + 1.5, CARD_W, this.cardHeight - 3, 16);
      await this.tweenPromise({ targets: visual.container, y: targetY, duration: 320, ease: "Back.Out" });
      if (token !== this.runToken) return;
    }
    const finished = finishReveal(this.state);
    this.state = finished;
    this.refresh();
    if (finished.phase === "won") this.finishLevelWon();
    else if (finished.phase === "lost") this.finishLevelLost();
  }

  private tweenPromise(config: Phaser.Types.Tweens.TweenBuilderConfig): Promise<void> {
    return new Promise((resolve) => this.tweens.add({ ...config, onComplete: () => resolve() }));
  }

  private finishLevelWon(): void {
    if (this.ended) return;
    this.ended = true;
    const n = this.level.n;
    const secs = Math.max(0, Math.round((Date.now() - this.startedAt) / 1000));
    track("level_end", { level: n, result: "won", moves: this.state.moves, secs });
    const key = String(n);
    const previous = this.save.bestMistakes[key];
    if (previous === undefined || this.state.mistakes < previous) this.save.bestMistakes[key] = this.state.mistakes;
    this.save.level = Math.max(this.save.level, n + 1);
    writeSave("zaman", this.save);
    const outcome = recordWin("zaman", n, this.state.stars);
    sfx.win();
    confetti(this);
    const stars = starLine(this.state.stars);
    const sub = `${stars}\n${formatText("levelWin", { level: n })} · ${formatText("mistakeCount", { moves: this.state.moves, mistakes: this.state.mistakes })}`;
    const buttons: OverlayButton[] = [
      { label: mapText("continue"), color: GREEN, onClick: () => openMap(this, "zaman", afterWin(n, outcome)) },
      { label: tr("share"), color: ACCENT, onClick: () => void this.shareScore() },
    ];
    this.overlayPanel = overlay(this, tr("wonTitle"), sub, buttons);
    this.overlayNext = { x: W / 2 - 135, y: this.scale.height / 2 + 110 };
    this.overlayShare = { x: W / 2 + 135, y: this.scale.height / 2 + 110 };
  }

  private finishLevelLost(): void {
    if (this.ended) return;
    this.ended = true;
    const n = this.level.n;
    const key = String(n);
    this.save.attempts[key] = (this.save.attempts[key] ?? 0) + 1;
    const attempts = this.save.attempts[key]!;
    const secs = Math.max(0, Math.round((Date.now() - this.startedAt) / 1000));
    track("level_end", { level: n, result: "lost", moves: this.state.moves, secs });
    writeSave("zaman", this.save);
    sfx.lose();
    const canSkip = attempts >= MAX_FAILS;
    const sub = `${formatText("mistakeCount", { moves: this.state.moves, mistakes: this.state.mistakes })}\n${tr("levelLost")}${canSkip ? `\n${currentLang() === "tr" ? "3 denemeden sonra ücretsiz atlayabilirsin." : "Free skip is available after 3 failed attempts."}` : `\n${tr("lossDetail")}`}`;
    const buttons: OverlayButton[] = [{ label: tr("retry"), color: ACCENT, onClick: () => this.scene.restart({ level: n }) }];
    if (canSkip) buttons.push({ label: tr("skip"), color: ORANGE, onClick: () => this.skipLevel() });
    this.overlayPanel = overlay(this, tr("lostTitle"), sub, buttons);
    this.overlayNext = buttons.length > 1 ? { x: W / 2 - 135, y: this.scale.height / 2 + 110 } : { x: W / 2, y: this.scale.height / 2 + 110 };
    this.overlayShare = buttons.length > 1 ? { x: W / 2 + 135, y: this.scale.height / 2 + 110 } : this.overlayNext;
  }

  private skipLevel(): void {
    const n = this.level.n;
    this.save.level = Math.max(this.save.level, n + 1);
    writeSave("zaman", this.save);
    const outcome = recordSkip("zaman", n);
    track("level_end", { level: n, result: "skip", moves: this.state.moves, secs: Math.max(0, Math.round((Date.now() - this.startedAt) / 1000)) });
    openMap(this, "zaman", afterSkip(n, outcome));
  }

  private async shareScore(): Promise<void> {
    const n = this.level.n;
    track("share_click", { level: n });
    const url = `${publicGameUrl("zaman")}?level=${n}`;
    const body = currentLang() === "tr" ? `Zaman Çizgisi'nde ${n}. seviyeyi tamamladım!` : `I completed Timeline level ${n}!`;
    const ok = await share(body, url, currentLang() === "tr" ? "Zaman Çizgisi — Enjoyment" : "Timeline — Enjoyment");
    if (ok) track("share_done", { level: n });
  }

  private botState(): Record<string, unknown> {
    const rect = this.game.canvas.getBoundingClientRect();
    const camera = this.cameras.main;
    const scaleX = rect.width / this.scale.width;
    const scaleY = rect.height / this.scale.height;
    const point = (x: number, y: number): Point => ({
      x: (x - camera.scrollX) * camera.zoom * scaleX,
      y: (y - camera.scrollY) * camera.zoom * scaleY,
    });
    const slots = this.slotPositions.map((slot) => point(slot.x, slot.y));
    return {
      level: this.state.level,
      phase: this.state.phase,
      moves: this.state.moves,
      mistakes: this.state.mistakes,
      cardsTotal: this.state.cardsTotal,
      timeline: this.state.timeline.map((event) => ({ id: event.id, year: event.year })),
      current: this.state.current ? { id: this.state.current.id, year: this.state.current.year } : null,
      correctSlot: this.state.correctSlot,
      screen: {
        slots,
        next: point(this.overlayNext.x, this.overlayNext.y),
        share: point(this.overlayShare.x, this.overlayShare.y),
      },
    };
  }

  private async autoplay(): Promise<void> {
    if (this.ended) return;
    ++this.runToken;
    this.state = oracleBot(this.level);
    this.refresh();
    if (this.state.phase === "won") this.finishLevelWon();
  }
}

function eventText(event: TimelineEvent, lang: "tr" | "en"): string {
  return lang === "tr" ? event.tr : event.en;
}
